/**
 * PriorProposalsPanel — Go/No-Go dashboard widget.
 *
 * Surfaces the top-N winning proposals from the consultant's library
 * (including cross-client matches) that share evaluation criteria with
 * the current pursuit. Auto-refreshes when Section M changes.
 *
 * Requirement #8 — v3.15.0.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Slider } from '../ui/slider';
import { Skeleton } from '../ui/skeleton';
import {
  FileSearch,
  RefreshCw,
  ExternalLink,
  Building2,
  Globe2,
  AlertCircle,
  Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const scoreBadgeClasses = (score) => {
  if (score >= 75) return 'bg-emerald-100 text-emerald-800 border-emerald-300';
  if (score >= 50) return 'bg-amber-100 text-amber-800 border-amber-300';
  return 'bg-slate-100 text-slate-700 border-slate-300';
};

const scoreLabel = (score) => {
  if (score >= 75) return 'Strong match';
  if (score >= 50) return 'Possible match';
  return 'Weak match';
};

export default function PriorProposalsPanel({ pursuitId }) {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [threshold, setThreshold] = useState(40);
  const [thresholdDraft, setThresholdDraft] = useState(40);
  const [savingThreshold, setSavingThreshold] = useState(false);

  const fetchMatches = useCallback(async (forceRefresh = false) => {
    if (!pursuitId) return;
    setLoading(true);
    try {
      const url =
        `${API_URL}/api/pursuits/${pursuitId}/gonogo/similar-eval-proposals` +
        (forceRefresh ? '?force_refresh=true' : '');
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const body = await res.json();
      setData(body);
      const t = Number(body.threshold ?? 40);
      setThreshold(t);
      setThresholdDraft(t);
    } catch (e) {
      console.error('PriorProposals fetch failed', e);
      toast.error('Failed to load prior-proposal matches');
    } finally {
      setLoading(false);
    }
  }, [pursuitId, getAuthHeaders]);

  useEffect(() => {
    fetchMatches();
  }, [fetchMatches]);

  const handleRecompute = async () => {
    if (!pursuitId) return;
    setRefreshing(true);
    try {
      const res = await fetch(
        `${API_URL}/api/pursuits/${pursuitId}/gonogo/similar-eval-proposals/recompute`,
        { method: 'POST', headers: getAuthHeaders() },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await fetchMatches();
      toast.success('Matches recomputed');
    } catch (e) {
      console.error(e);
      toast.error('Recompute failed');
    } finally {
      setRefreshing(false);
    }
  };

  const handleThresholdCommit = async (val) => {
    const next = Array.isArray(val) ? val[0] : val;
    setThresholdDraft(next);
    if (next === threshold) return;
    setSavingThreshold(true);
    try {
      const res = await fetch(
        `${API_URL}/api/pursuits/${pursuitId}/gonogo/similar-eval-proposals/threshold`,
        {
          method: 'POST',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ threshold: next }),
        },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      setData((prev) => ({
        ...(prev || {}),
        results: body.results,
        threshold: body.threshold,
      }));
      setThreshold(next);
    } catch (e) {
      console.error(e);
      toast.error('Could not update threshold');
      setThresholdDraft(threshold);
    } finally {
      setSavingThreshold(false);
    }
  };

  const sectionMMissing = data && !data.section_m_present;
  const phaseNotApplicable = data && data.applicable === false;
  const phaseLabel = data?.procurement_phase
    ? data.procurement_phase.replace(/_/g, ' ')
    : '';
  const results = useMemo(() => data?.results || [], [data]);

  return (
    <Card
      className="border-purple-100 shadow-sm"
      data-testid="gonogo-prior-proposals-panel"
    >
      <CardHeader className="bg-gradient-to-r from-purple-50 to-fuchsia-50 border-b border-purple-100">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="flex items-center gap-2 text-purple-900">
            <FileSearch className="w-5 h-5" />
            Prior Proposals — Similar Evaluation Criteria
            {results.length > 0 && (
              <Badge
                variant="outline"
                className="ml-2 bg-purple-100 text-purple-800 border-purple-300"
                data-testid="prior-proposals-match-count"
              >
                {results.length} {results.length === 1 ? 'match' : 'matches'}
              </Badge>
            )}
          </CardTitle>
          <Button
            size="sm"
            variant="outline"
            onClick={handleRecompute}
            disabled={refreshing || loading}
            data-testid="prior-proposals-recompute-btn"
          >
            <RefreshCw
              className={`w-4 h-4 mr-1 ${refreshing ? 'animate-spin' : ''}`}
            />
            Recompute
          </Button>
        </div>
        <p className="text-xs text-slate-600 mt-1">
          Matches winning proposals across your library against this pursuit's
          Section M factors, weights, and method. Cross-client matches honor
          NDA flags on each proposal.
        </p>
      </CardHeader>

      <CardContent className="p-4 space-y-4">
        {/* Threshold slider */}
        <div className="flex flex-wrap items-center gap-3 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
          <div className="flex items-center gap-2 min-w-fit">
            <span className="text-xs font-medium text-slate-600">
              Match floor
            </span>
            <Badge
              variant="outline"
              className="bg-white"
              data-testid="prior-proposals-threshold-badge"
            >
              {thresholdDraft}
            </Badge>
          </div>
          <Slider
            className="flex-1 min-w-[200px]"
            min={0}
            max={100}
            step={5}
            value={[thresholdDraft]}
            onValueChange={(v) => setThresholdDraft(v[0])}
            onValueCommit={handleThresholdCommit}
            disabled={savingThreshold || loading}
            data-testid="prior-proposals-threshold-slider"
          />
          <span className="text-xs text-slate-500">
            Below this score, matches are hidden.
          </span>
        </div>

        {/* Empty / loading / data states */}
        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        ) : phaseNotApplicable ? (
          <div
            className="flex items-start gap-2 p-3 bg-sky-50 border border-sky-200 rounded-md"
            data-testid="prior-proposals-empty-phase-not-applicable"
          >
            <AlertCircle className="w-5 h-5 text-sky-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-sky-900 flex-1">
              <strong>Evaluation criteria don't apply at this phase{phaseLabel ? ` (${phaseLabel})` : ''}.</strong>{' '}
              Section M lives on the follow-on RFP — the matcher will activate
              automatically once you advance this pursuit to a solicitation phase.
              In the meantime, the <strong>Cross-Tool Coverage Map</strong> can
              surface similar past RFI responses and capability statements by
              NAICS &amp; agency.
            </div>
          </div>
        ) : sectionMMissing ? (
          <div
            className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md"
            data-testid="prior-proposals-empty-no-section-m"
          >
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div className="text-sm text-amber-900">
              <strong>Section M not extracted yet.</strong> Run the AI Shredder
              on this pursuit's RFP so the system can identify evaluation
              factors before scoring prior proposals.
            </div>
          </div>
        ) : results.length === 0 ? (
          <div
            className="text-sm text-slate-600 p-4 bg-slate-50 border border-slate-200 rounded-md"
            data-testid="prior-proposals-empty-no-matches"
          >
            No prior proposals in your library cross the
            <strong> {thresholdDraft}</strong> match floor. Lower the floor, or
            upload more winning proposals — and click <em>Recompute</em> to
            re-run.
          </div>
        ) : (
          <div className="space-y-3" data-testid="prior-proposals-results-list">
            {results.map((r, idx) => (
              <div
                key={r.id}
                className="border border-slate-200 rounded-lg p-3 hover:border-purple-300 transition-colors"
                data-testid={`prior-proposal-row-${idx}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className="text-sm font-semibold text-slate-900 truncate"
                        data-testid={`prior-proposal-title-${idx}`}
                      >
                        {r.title || '(untitled)'}
                      </span>
                      {r.is_cross_client && (
                        <Badge
                          variant="outline"
                          className="bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200 text-[10px]"
                          data-testid={`prior-proposal-cross-client-${idx}`}
                        >
                          <Globe2 className="w-3 h-3 mr-0.5" />
                          Cross-client
                        </Badge>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-slate-500">
                      {r.agency && (
                        <span className="flex items-center gap-1">
                          <Building2 className="w-3 h-3" /> {r.agency}
                        </span>
                      )}
                      {r.naics_code && <span>NAICS {r.naics_code}</span>}
                      {r.evaluation_method && (
                        <span>Method: {r.evaluation_method}</span>
                      )}
                      {r.contract_value && (
                        <span>
                          ${Number(r.contract_value).toLocaleString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <Badge
                    variant="outline"
                    className={scoreBadgeClasses(r.score)}
                    data-testid={`prior-proposal-match-score-${idx}`}
                  >
                    <Sparkles className="w-3 h-3 mr-1" />
                    {Math.round(r.score)} · {scoreLabel(r.score)}
                  </Badge>
                </div>

                {/* Reason chips */}
                {Array.isArray(r.match_reasons) && r.match_reasons.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {r.match_reasons.map((reason, i) => (
                      <Badge
                        key={i}
                        variant="outline"
                        className="bg-purple-50 text-purple-700 border-purple-200 text-[10px] font-normal"
                        data-testid={`prior-proposal-reason-${idx}-${i}`}
                      >
                        {reason}
                      </Badge>
                    ))}
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 mt-3">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      // Deep-link into the Client Profile → Winning Proposals
                      // section. The proposal id is exposed via the row id.
                      window.location.hash = `winning-proposal-${r.id}`;
                      toast.info(
                        'Open Manage Documents → Winning Proposals to view the full attachment.',
                      );
                    }}
                    data-testid={`prior-proposal-open-btn-${idx}`}
                  >
                    <ExternalLink className="w-3.5 h-3.5 mr-1" />
                    Open Proposal
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
